import { useEffect, useRef, useState } from 'react';
import type { Woning } from '@/types/domain';
import { getWoningService } from '@/services/serviceFactory';
import { useDebounce } from '@/hooks/useDebounce';
import { adresRegel } from '@/utils/format';
import { afkoppelStatus, type KaartStatus } from '@/types/domain';
import { isHuurwoning } from '@/data/huurwoningen';
import { StatusStip } from '@/components/ui/StatusStip';
import { useApp } from '@/state/AppContext';
import { ligtInPlangebied } from '@/data/plangebied';
import { LegeStaat, Spinner } from '@/components/ui/Feedback';
import { SluitIcoon, ZoekIcoon } from '@/components/layout/Iconen';
import './map.css';

interface ZoekbalkProps {
  bijKeuze: (woning: Woning) => void;
}

/** Zoeken op straat + huisnummer; resultaten komen uit de actieve BAG-bron. */
export function Zoekbalk({ bijKeuze }: ZoekbalkProps): JSX.Element {
  const [term, setTerm] = useState('');
  const [resultaten, setResultaten] = useState<Woning[]>([]);
  const [bezig, setBezig] = useState(false);
  const [open, setOpen] = useState(false);
  const vertraagdeTerm = useDebounce(term, 350);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let geannuleerd = false;

    if (vertraagdeTerm.trim().length < 2) {
      setResultaten([]);
      setBezig(false);
      return;
    }

    setBezig(true);
    getWoningService()
      .zoek(vertraagdeTerm)
      .then((treffers) => {
        if (!geannuleerd) setResultaten(treffers);
      })
      .catch(() => {
        if (!geannuleerd) setResultaten([]);
      })
      .finally(() => {
        if (!geannuleerd) setBezig(false);
      });

    return () => {
      geannuleerd = true;
    };
  }, [vertraagdeTerm]);

  // Klik buiten de zoekbalk sluit de resultatenlijst.
  useEffect(() => {
    const bijKlikBuiten = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', bijKlikBuiten);
    return () => document.removeEventListener('mousedown', bijKlikBuiten);
  }, []);

  const kies = (woning: Woning) => {
    bijKeuze(woning);
    setOpen(false);
    setTerm(adresRegel(woning.adres));
  };

  const { registraties } = useApp();

  const toonLijst = open && vertraagdeTerm.trim().length >= 2;

  // Binnen en buiten het plangebied gescheiden tonen; binnen staat altijd eerst.
  const binnen = resultaten.filter((w) => ligtInPlangebied(w.latitude, w.longitude));
  const buiten = resultaten.filter((w) => !ligtInPlangebied(w.latitude, w.longitude));

  const statusVan = (woning: Woning): KaartStatus =>
    afkoppelStatus(
      registraties.find((r) => r.bagId && r.bagId === woning.bagId),
      isHuurwoning(woning.adres)
    );

  return (
    <div className="zoekbalk" ref={container}>
      <div className="zoekbalk__veld">
        <ZoekIcoon />
        <input
          type="search"
          value={term}
          placeholder="Zoek straat en huisnummer"
          aria-label="Zoek op straat en huisnummer"
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {bezig && <Spinner />}
        {term && !bezig && (
          <button
            type="button"
            className="zoekbalk__wissen"
            aria-label="Zoekopdracht wissen"
            onClick={() => {
              setTerm('');
              setResultaten([]);
            }}
          >
            <SluitIcoon />
          </button>
        )}
      </div>

      {toonLijst && (
        <div className="zoekbalk__resultaten">
          {resultaten.length === 0 && !bezig && (
            <LegeStaat icoon={<ZoekIcoon />} titel="Geen adres gevonden">
              <p>
                Controleer de straatnaam, of zet een punt op de kaart als de woning nog niet
                in de BAG staat.
              </p>
            </LegeStaat>
          )}

          {binnen.map((woning) => (
            <Resultaat key={sleutelVan(woning)} woning={woning} status={statusVan(woning)} bijKeuze={kies} />
          ))}

          {/* Buiten het plangebied als eigen groep, niet als merkje achter de
              plaatsnaam. Straatnamen komen in tientallen plaatsen voor, en een
              coach die snel tikt leest die regel niet. */}
          {buiten.length > 0 && (
            <>
              <div className="zoekbalk__groepkop">Buiten het plangebied</div>
              {buiten.map((woning) => (
                <Resultaat
                  key={sleutelVan(woning)}
                  woning={woning}
                  status={statusVan(woning)}
                  buiten
                  bijKeuze={kies}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Sleutel die ook werkt voor punten zonder BAG-id. */
function sleutelVan(woning: Woning): string {
  return woning.bagId || `${woning.latitude},${woning.longitude}`;
}

/** Eén zoekresultaat: statusstip, adres, plaats. */
function Resultaat({
  woning,
  status,
  buiten = false,
  bijKeuze,
}: {
  woning: Woning;
  status: KaartStatus;
  buiten?: boolean;
  bijKeuze: (woning: Woning) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`zoekbalk__resultaat${buiten ? ' zoekbalk__resultaat--buiten' : ''}`}
      onClick={() => bijKeuze(woning)}
    >
      <StatusStip status={status} formaat={12} />
      <span className="zoekbalk__tekst">
        <span className="zoekbalk__adres">{adresRegel(woning.adres)}</span>
        <span className="zoekbalk__plaats">
          {woning.adres.postcode} {woning.adres.plaats}
        </span>
      </span>
    </button>
  );
}
